/**
 * Checkout Service
 * Handles Stripe checkout integration
 */

import { authService } from '../auth/auth-service.js';
import { SUPABASE_URL, STRIPE_PUBLISHABLE_KEY } from '../config/config.js';
import { trackCheckoutStarted } from '../app/analytics/event-tracker.js';

// Price IDs - these must match your Stripe dashboard
// Update these after creating products in Stripe
export const PRICE_IDS = {
  PRO_MONTHLY: 'price_pro_monthly',      // Replace with actual Stripe price ID
  PRO_ANNUAL: 'price_pro_annual',        // Replace with actual Stripe price ID
  ENTERPRISE_MONTHLY: 'price_enterprise_monthly', // Replace with actual Stripe price ID
  CREDITS_50: 'price_credits_50',        // Replace with actual Stripe price ID
  CREDITS_200: 'price_credits_200',      // Replace with actual Stripe price ID
};

class CheckoutService {
  constructor() {
    this.stripe = null;
  }

  /**
   * Initialize Stripe.js
   */
  async init() {
    if (this.stripe) return;

    // Wait for Stripe to be available (may load asynchronously)
    if (typeof Stripe === 'undefined') {
      await this.waitForStripe();
    }

    if (typeof Stripe === 'undefined') {
      console.error('[CheckoutService] Stripe.js failed to load');
      return;
    }

    if (!STRIPE_PUBLISHABLE_KEY || STRIPE_PUBLISHABLE_KEY === 'pk_test_YOUR_STRIPE_KEY') {
      console.warn('[CheckoutService] Stripe not configured');
      return;
    }

    this.stripe = Stripe(STRIPE_PUBLISHABLE_KEY);
  }

  /**
   * Wait for Stripe.js to be available
   */
  waitForStripe(timeout = 5000) {
    return new Promise((resolve) => {
      if (typeof Stripe !== 'undefined') {
        resolve();
        return;
      }

      const startTime = Date.now();
      const check = () => {
        if (typeof Stripe !== 'undefined') {
          resolve();
        } else if (Date.now() - startTime > timeout) {
          resolve(); // Timeout - let init() handle the undefined case
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  }

  /**
   * Create a checkout session and redirect to Stripe
   * @param {string} priceId - Stripe price ID
   * @param {Object} options - Options
   * @param {string} options.successUrl - Success redirect URL
   * @param {string} options.cancelUrl - Cancel redirect URL
   * @param {string} options.tier - Tier being purchased (for tracking)
   * @param {string} options.billingPeriod - Billing period (for tracking)
   * @param {number} options.price - Price in dollars (for tracking)
   */
  async checkout(priceId, options = {}) {
    if (!authService.isAuthenticated()) {
      throw new Error('Please log in to subscribe');
    }

    const accessToken = authService.getAccessToken();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    // Track checkout started
    if (options.tier) {
      trackCheckoutStarted(options.tier, options.billingPeriod || 'unknown', options.price || 0);
    }

    // Create checkout session via Edge Function
    const response = await fetch(`${SUPABASE_URL}/functions/v1/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        priceId,
        successUrl: options.successUrl || `${window.location.origin}?checkout=success`,
        cancelUrl: options.cancelUrl || `${window.location.origin}?checkout=canceled`,
      }),
    });

    if (!response.ok) {
      let message = `Server error: ${response.status}`;
      try { const body = await response.json(); message = body.error || message; } catch {}
      throw new Error(message);
    }

    const { url } = await response.json();

    // Redirect to Stripe Checkout
    window.location.href = url;
  }

  /**
   * Subscribe to Pro monthly
   */
  async subscribeProMonthly() {
    return this.checkout(PRICE_IDS.PRO_MONTHLY);
  }

  /**
   * Subscribe to Pro annual
   */
  async subscribeProAnnual() {
    return this.checkout(PRICE_IDS.PRO_ANNUAL);
  }

  /**
   * Subscribe to Enterprise
   */
  async subscribeEnterprise() {
    return this.checkout(PRICE_IDS.ENTERPRISE_MONTHLY);
  }

  /**
   * Buy 50 credits
   */
  async buyCredits50() {
    return this.checkout(PRICE_IDS.CREDITS_50);
  }

  /**
   * Buy 200 credits
   */
  async buyCredits200() {
    return this.checkout(PRICE_IDS.CREDITS_200);
  }

  /**
   * Open Stripe Customer Portal for subscription management
   */
  async openCustomerPortal() {
    if (!authService.isAuthenticated()) {
      throw new Error('Please log in first');
    }

    const accessToken = authService.getAccessToken();

    const response = await fetch(`${SUPABASE_URL}/functions/v1/create-portal-session`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        returnUrl: window.location.href,
      }),
    });

    if (!response.ok) {
      let message = `Server error: ${response.status}`;
      try { const body = await response.json(); message = body.error || message; } catch {}
      throw new Error(message);
    }

    const { url } = await response.json();
    window.location.href = url;
  }
}

// Export singleton instance
export const checkoutService = new CheckoutService();
